import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import ReactApexChart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import {
  pmService,
  type DenialRate,
  type DenialRateResult,
  type PayerPriority,
} from "../../services/pm.service";
import { Select } from "../ui/Select";
import { DatePicker } from "../ui/DatePicker";
import { computePeriodRange, type PmPeriodType } from "../../utils/period";

type GroupBy = "insurer" | "cpt";
type SortDir = "desc" | "asc";
type SortBy = "denial_rate" | "denials" | "total_claims";

interface FilterState {
  type: PmPeriodType;
  customFrom: string;
  customTo: string;
  groupBy: GroupBy;
  sortBy: SortBy;
  sortDir: SortDir;
  priority: PayerPriority;
}

const defaultFilter = (): FilterState => {
  const { dateFrom, dateTo } = computePeriodRange("current_month");
  return {
    type: "current_month",
    customFrom: dateFrom,
    customTo: dateTo,
    groupBy: "insurer",
    sortBy: "denials",
    sortDir: "desc",
    priority: "primary",
  };
};

const filtersEqual = (a: FilterState, b: FilterState) =>
  a.type === b.type &&
  a.customFrom === b.customFrom &&
  a.customTo === b.customTo &&
  a.groupBy === b.groupBy &&
  a.sortBy === b.sortBy &&
  a.sortDir === b.sortDir &&
  a.priority === b.priority;

interface AggRow {
  label: string;
  totalClaims: number;
  deniedClaims: number;
  denialRate: number;
}

function aggregate(
  data: DenialRate[],
  groupBy: GroupBy,
  sortBy: SortBy,
  sortDir: SortDir
): AggRow[] {
  const rows: AggRow[] = data.map((d) => ({
    label: groupBy === "insurer" ? d.insurer : d.cptCode,
    totalClaims: d.totalClaims,
    deniedClaims: d.deniedClaims,
    denialRate: d.denialRate,
  }));
  const sortKey: Record<SortBy, keyof AggRow> = {
    denial_rate: "denialRate",
    denials: "deniedClaims",
    total_claims: "totalClaims",
  };
  const key = sortKey[sortBy];
  rows.sort((a, b) =>
    sortDir === "desc"
      ? (b[key] as number) - (a[key] as number)
      : (a[key] as number) - (b[key] as number)
  );
  return rows;
}

interface TooltipState {
  row: AggRow;
  x: number;
  y: number;
}

const TOOLTIP_OFFSET = 12;
const VISIBLE_HEIGHT = 12 * 32 + 50;

export const DenialRateWidget: React.FC = () => {
  const [pending, setPending] = useState<FilterState>(defaultFilter);
  const [applied, setApplied] = useState<FilterState>(defaultFilter);
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const rowsRef = useRef<AggRow[]>([]);
  const chartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const hide = () => setTooltip(null);
    window.addEventListener("scroll", hide, { capture: true, passive: true });
    return () => window.removeEventListener("scroll", hide, { capture: true });
  }, []);

  const clampedPos = useCallback((x: number, y: number) => {
    const W = 230;
    const H = 100;
    if (!chartRef.current) return { top: y + TOOLTIP_OFFSET, left: x + TOOLTIP_OFFSET };
    const r = chartRef.current.getBoundingClientRect();
    return {
      top: Math.min(Math.max(y + TOOLTIP_OFFSET, r.top), r.bottom - H),
      left: Math.min(Math.max(x + TOOLTIP_OFFSET, r.left), r.right - W),
    };
  }, []);

  const hasChanges = !filtersEqual(pending, applied);

  const { dateFrom, dateTo } = computePeriodRange(
    applied.type,
    applied.customFrom,
    applied.customTo
  );

  const { data, isLoading } = useQuery<DenialRateResult>({
    queryKey: ["pm-denials", dateFrom, dateTo, applied.groupBy, applied.priority],
    queryFn: () =>
      pmService.getDenialsByInsurerCpt(dateFrom, dateTo, applied.groupBy, applied.priority),
  });

  const allRows = useMemo(
    () => aggregate(data?.rows ?? [], applied.groupBy, applied.sortBy, applied.sortDir),
    [data, applied.groupBy, applied.sortBy, applied.sortDir]
  );

  const metricConfig: Record<
    SortBy,
    {
      getValue: (r: AggRow) => number;
      name: string;
      labelFormatter: (val: number) => string;
      xFormatter: (val: string) => string;
      xMax: number | undefined;
      filterRow: (r: AggRow) => boolean;
    }
  > = {
    denial_rate: {
      getValue: (r) => r.denialRate,
      name: "Denial Rate",
      labelFormatter: (val) => `${parseFloat(Number(val).toFixed(2))}%`,
      xFormatter: (val) => (Number(val) <= 100 ? `${parseFloat(Number(val).toFixed(2))}%` : ""),
      xMax: 120,
      filterRow: (r) => r.denialRate > 0,
    },
    denials: {
      getValue: (r) => r.deniedClaims,
      name: "Denials",
      labelFormatter: (val) => val.toLocaleString(),
      xFormatter: (val) => Number(val).toLocaleString(),
      xMax: undefined,
      filterRow: (r) => r.deniedClaims > 0,
    },
    total_claims: {
      getValue: (r) => r.totalClaims,
      name: "Total Claims",
      labelFormatter: (val) => val.toLocaleString(),
      xFormatter: (val) => Number(val).toLocaleString(),
      xMax: undefined,
      filterRow: (r) => r.totalClaims > 0,
    },
  };

  const cfg = metricConfig[applied.sortBy];
  const rows = allRows.filter(cfg.filterRow);
  rowsRef.current = rows;

  const categories = rows.map((r) => r.label);
  const chartHeight = Math.max(VISIBLE_HEIGHT, rows.length * 32 + 50);

  const totalClaims = data?.totalClaims ?? 0;
  const totalDenied = data?.totalDenied ?? 0;

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (tooltip) {
        setTooltip((prev) => prev && { ...prev, x: e.clientX, y: e.clientY });
      }
    },
    [tooltip]
  );

  const chartOptions: ApexOptions = {
    chart: {
      type: "bar",
      toolbar: {
        show: true,
        tools: {
          download: true,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false,
          selection: false,
        },
        offsetX: -10,
        offsetY: 5,
      },
      animations: { enabled: false },
      offsetY: -20,
      events: {
        dataPointMouseEnter: (
          _e: MouseEvent,
          _chart: unknown,
          opts: { dataPointIndex: number }
        ) => {
          const row = rowsRef.current[opts.dataPointIndex];
          if (row) {
            setTooltip({ row, x: _e.clientX, y: _e.clientY });
          }
        },
        dataPointMouseLeave: () => {
          setTooltip(null);
        },
      },
    },
    plotOptions: {
      bar: { horizontal: true, barHeight: "50%", dataLabels: { position: "top" } },
    },
    dataLabels: {
      enabled: true,
      formatter: (val: number) => cfg.labelFormatter(val),
      style: { fontSize: "11px", colors: ["#699CBB"], fontWeight: "bold" },
      offsetX: 20,
    },
    colors: ["#699CBB"],
    xaxis: {
      categories,
      min: 0,
      max: cfg.xMax,
      labels: {
        formatter: cfg.xFormatter,
        style: { fontSize: "11px", colors: "#6b7280" },
      },
      axisBorder: { show: true, color: "#BCBDC5" },
      axisTicks: { show: true, color: "#BCBDC5" },
    },
    yaxis: {
      title: { text: undefined },
      labels: { style: { fontSize: "11px" }, maxWidth: 200 },
    },
    tooltip: { enabled: false },
    grid: {
      strokeDashArray: 3,
      xaxis: { lines: { show: true } },
      yaxis: { lines: { show: false } },
      padding: { right: 50 },
    },
    legend: { show: false },
  };

  return (
    <div className="bg-white border-2 border-border-gray">
      {/* Header */}
      <div className="bg-primary-blue text-white px-4 md:px-6 py-3">
        <h3 className="font-bold text-lg">Denial Rate by Insurer & CPT</h3>
        <span className="text-xs sm:text-sm">Claims denied per insurer and procedure code</span>
      </div>

      {/* Controls */}
      <div className="p-4 bg-gray-50 border-b-2 border-border-gray flex flex-wrap gap-3 items-end">
        {/* Period */}
        <div className="flex-shrink-0">
          <label className="block text-xs font-medium text-gray-700 mb-1">Period</label>
          <Select
            value={pending.type}
            onChange={(e) =>
              setPending((prev) => ({ ...prev, type: e.target.value as PmPeriodType }))
            }
          >
            <option value="current_week">Current Week</option>
            <option value="previous_week">Previous Week</option>
            <option value="current_month">Current Month</option>
            <option value="previous_month">Previous Month</option>
            <option value="last_90_days">Last 90 Days</option>
            <option value="year_to_date">Year to Date</option>
            <option value="last_year">Last Year</option>
            <option value="custom">Custom Period</option>
          </Select>
        </div>

        {pending.type === "custom" && (
          <>
            <div className="flex-shrink-0">
              <label className="block text-xs font-medium text-gray-700 mb-1">Start date</label>
              <DatePicker
                value={pending.customFrom}
                maxDate={pending.customTo || undefined}
                onChange={(v) => setPending((prev) => ({ ...prev, customFrom: v }))}
              />
            </div>
            <div className="flex-shrink-0">
              <label className="block text-xs font-medium text-gray-700 mb-1">End date</label>
              <DatePicker
                value={pending.customTo}
                minDate={pending.customFrom || undefined}
                onChange={(v) => setPending((prev) => ({ ...prev, customTo: v }))}
              />
            </div>
          </>
        )}

        {/* Group by toggler */}
        <div className="flex-shrink-0">
          <label className="block text-xs font-medium text-gray-700 mb-1">Group by</label>
          <div className="flex border-2 border-border-gray h-10">
            <button
              type="button"
              onClick={() => setPending((prev) => ({ ...prev, groupBy: "insurer" }))}
              className={`px-4 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                pending.groupBy === "insurer"
                  ? "bg-primary-blue text-white"
                  : "bg-white text-black hover:bg-gray-50"
              }`}
            >
              Insurer
            </button>
            <button
              type="button"
              onClick={() => setPending((prev) => ({ ...prev, groupBy: "cpt" }))}
              className={`px-4 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap border-l-2 border-border-gray ${
                pending.groupBy === "cpt"
                  ? "bg-primary-blue text-white"
                  : "bg-white text-black hover:bg-gray-50"
              }`}
            >
              CPT Code
            </button>
          </div>
        </div>

        {/* Sort by toggler */}
        <div className="flex-shrink-0">
          <label className="block text-xs font-medium text-gray-700 mb-1">Sort by</label>
          <div className="flex border-2 border-border-gray h-10">
            {(
              [
                { value: "denial_rate", label: "Denial Rate" },
                { value: "denials", label: "Denials" },
                { value: "total_claims", label: "Total Claims" },
              ] as { value: SortBy; label: string }[]
            ).map(({ value, label }, i) => (
              <button
                key={value}
                type="button"
                onClick={() => setPending((prev) => ({ ...prev, sortBy: value }))}
                className={`px-4 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                  i > 0 ? "border-l-2 border-border-gray" : ""
                } ${
                  pending.sortBy === value
                    ? "bg-primary-blue text-white"
                    : "bg-white text-black hover:bg-gray-50"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Order toggler */}
        <div className="flex-shrink-0">
          <label className="block text-xs font-medium text-gray-700 mb-1">Order</label>
          <div className="flex border-2 border-border-gray h-10">
            <button
              type="button"
              onClick={() => setPending((prev) => ({ ...prev, sortDir: "desc" }))}
              className={`px-4 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                pending.sortDir === "desc"
                  ? "bg-primary-blue text-white"
                  : "bg-white text-black hover:bg-gray-50"
              }`}
            >
              Descending
            </button>
            <button
              type="button"
              onClick={() => setPending((prev) => ({ ...prev, sortDir: "asc" }))}
              className={`px-4 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap border-l-2 border-border-gray ${
                pending.sortDir === "asc"
                  ? "bg-primary-blue text-white"
                  : "bg-white text-black hover:bg-gray-50"
              }`}
            >
              Ascending
            </button>
          </div>
        </div>

        {/* Payor dropdown */}
        <div className="flex-shrink-0">
          <label className="block text-xs font-medium text-gray-700 mb-1">Payor</label>
          <Select
            value={pending.priority}
            onChange={(e) =>
              setPending((prev) => ({ ...prev, priority: e.target.value as PayerPriority }))
            }
          >
            <option value="all">All</option>
            <option value="primary">Primary</option>
            <option value="secondary">Secondary</option>
            <option value="primary_secondary">Primary+Secondary</option>
            <option value="tertiary">Tertiary</option>
          </Select>
        </div>

        <button
          type="button"
          onClick={() => setApplied(pending)}
          disabled={!hasChanges}
          className="h-10 px-6 bg-primary-blue text-white text-xs sm:text-sm hover:bg-indigo-darker-blue disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap flex-shrink-0 transition-colors"
        >
          Apply
        </button>
      </div>

      {/* Chart */}
      <div>
        {isLoading && <div className="text-center py-8 text-gray-500">Loading denial data...</div>}

        {!isLoading && rows.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No denials found for the selected period.
          </div>
        )}

        {!isLoading && rows.length > 0 && (
          <>
            <div className="bg-gray-50 border-b-2 border-border-gray px-4 md:px-6 py-2 text-xs font-bold text-gray-700 uppercase tracking-wide">
              {totalDenied.toLocaleString()} denied / {totalClaims.toLocaleString()} total claims
            </div>
            <div
              ref={chartRef}
              onMouseMove={handleMouseMove}
              style={{ maxHeight: `${VISIBLE_HEIGHT}px`, overflowY: "auto", overflowX: "hidden" }}
            >
              <ReactApexChart
                options={chartOptions}
                series={[{ name: cfg.name, data: rows.map(cfg.getValue) }]}
                type="bar"
                height={chartHeight}
              />
            </div>
          </>
        )}
      </div>

      {/* Portal tooltip — renders in document.body, never clipped */}
      {tooltip &&
        createPortal(
          <div
            style={{
              position: "fixed",
              ...clampedPos(tooltip.x, tooltip.y),
              zIndex: 9999,
              pointerEvents: "none",
            }}
            className="bg-white border border-gray-200 shadow-lg rounded px-3 py-2 text-xs"
          >
            <div className="font-semibold text-gray-800 mb-1">{tooltip.row.label}</div>
            <div className="text-gray-600">
              Denial rate:{" "}
              <span className="font-semibold text-gray-800">
                {parseFloat(tooltip.row.denialRate.toFixed(2))}%
              </span>
            </div>
            <div className="text-gray-600">
              Denied:{" "}
              <span className="font-semibold text-gray-800">
                {tooltip.row.deniedClaims.toLocaleString()}
              </span>
              {" / "}
              {tooltip.row.totalClaims.toLocaleString()}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};
